import type { SurveyQuestion } from '../../../fetchSurvey/types';
import type { MatrixRowAnswers, SurveyAnswers } from '../../../submitSurvey/types';
import { isMatrixRowAnswers } from '../../fields/matrixQuestionUtils';
import type { LogicContext } from '../../types';
import { getVisibleScaleColumns, getVisibleStatementRows } from '../../visibility';
import { hasScaleColumnAnswerLogic } from '../../visibility/questionAccess';

export function cellUsesHiddenColumn(
  cellValue: string | number | null | Array<string | number | null> | undefined,
  visibleColumnIds: Set<string>
): boolean {
  if (cellValue === undefined || cellValue === null || cellValue === '') {
    return false;
  }

  if (Array.isArray(cellValue)) {
    return cellValue.some(value => !visibleColumnIds.has(String(value)));
  }

  return !visibleColumnIds.has(String(cellValue));
}

export function sanitizeMatrixColumnValues(
  answer: MatrixRowAnswers,
  visibleColumnIds: Set<string>
): MatrixRowAnswers | undefined {
  const nextRowMap: MatrixRowAnswers = { ...answer };
  let changed = false;

  Object.entries(nextRowMap).forEach(([rowId, cellValue]) => {
    if (cellValue === undefined || cellValue === null) {
      return;
    }

    if (Array.isArray(cellValue)) {
      const filtered = cellValue.filter(value => visibleColumnIds.has(String(value)));
      if (filtered.length !== cellValue.length) {
        if (filtered.length === 0) {
          delete nextRowMap[rowId];
        } else {
          nextRowMap[rowId] = filtered;
        }
        changed = true;
      }
      return;
    }

    if (!visibleColumnIds.has(String(cellValue))) {
      delete nextRowMap[rowId];
      changed = true;
    }
  });

  if (!changed) {
    return answer;
  }

  return Object.keys(nextRowMap).length > 0 ? nextRowMap : undefined;
}

export function sanitizeMatrixRowMap(
  answer: MatrixRowAnswers,
  visibleRowIds: Set<string>,
  visibleColumnIds: Set<string>,
  hasRowFiltering: boolean,
  hasColumnFiltering: boolean
): MatrixRowAnswers | undefined {
  const nextRowMap: MatrixRowAnswers = { ...answer };

  if (hasRowFiltering) {
    Object.keys(nextRowMap).forEach(rowId => {
      if (!visibleRowIds.has(rowId)) {
        delete nextRowMap[rowId];
      }
    });
  }

  if (hasColumnFiltering && Object.keys(nextRowMap).length > 0) {
    Object.entries(nextRowMap).forEach(([rowId, cellValue]) => {
      if (cellUsesHiddenColumn(cellValue, visibleColumnIds)) {
        if (Array.isArray(cellValue)) {
          const filtered = cellValue.filter(
            (value): value is string | number => value !== null && visibleColumnIds.has(String(value))
          );
          if (filtered.length === 0) {
            delete nextRowMap[rowId];
          } else {
            nextRowMap[rowId] = filtered;
          }
        } else {
          delete nextRowMap[rowId];
        }
      }
    });
  }

  if (Object.keys(nextRowMap).length === 0) {
    return undefined;
  }

  if (hasColumnFiltering) {
    return sanitizeMatrixColumnValues(nextRowMap, visibleColumnIds);
  }

  return nextRowMap;
}

export function sanitizeMatrixQuestionAnswer(
  question: SurveyQuestion,
  answer: SurveyAnswers[string] | undefined,
  context: LogicContext
): SurveyAnswers[string] | undefined {
  if (!isMatrixRowAnswers(answer)) {
    return answer;
  }

  const visibleRows = getVisibleStatementRows(question, context);
  const allRows = (question as SurveyQuestion & { statementRows?: { id: string }[] }).statementRows ?? [];
  const visibleScaleColumns = hasScaleColumnAnswerLogic(question.type)
    ? getVisibleScaleColumns(question, context)
    : [];
  const allScaleColumns =
    (question as SurveyQuestion & { scaleColumns?: { id: string }[] }).scaleColumns ?? [];

  const hasRowFiltering = allRows.length > 0 && visibleRows.length !== allRows.length;
  const hasColumnFiltering =
    allScaleColumns.length > 0 && visibleScaleColumns.length !== allScaleColumns.length;

  if (!hasRowFiltering && !hasColumnFiltering) {
    return answer;
  }

  return sanitizeMatrixRowMap(
    answer,
    new Set(visibleRows.map(row => row.id)),
    new Set(visibleScaleColumns.map(column => column.id)),
    hasRowFiltering,
    hasColumnFiltering
  );
}
